import * as React from 'react';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Primary, Secondary } from 'rcd-cpp-ui/ui/button';
import { Alert } from 'rcd-cpp-ui/ui/alert/Alert';
import { Metadata, Schema, SchemaField } from '../../../types';
import { ValidationResponse } from '../../../types/ValidationResponse';
import { TreeField } from '../TreeField/treeField';
import { TreeFieldUtils } from '../../../utils/treeFieldUtils';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { Node } from '../../TreeSelect/treeSelect';
import {
  ModalPushResponseContext,
  getValidationErrors
} from '../FieldGroup/fieldGroup';
import './additionalQuestionsModal.scss';
import { NodeType } from '../../../enums/NodeType';
import { useActions } from '../metadataFormActions';
import { ItemType } from '../../../enums/ItemType';
import { ThruCd } from '../../../enums/ThruCd';
import { ValidationMode } from '../../../enums/ValidationMode';
import { FieldRequiredValue } from '../../../enums/FieldRequiredValue';
import {
  isWIPProductionPhase,
  isPublishedOrCompletedInCPP
} from '../../../utils/status';

type FieldUpdate = { field: SchemaField; value: Metadata[] };

const ALL_DERIVED_FIELDS = new Set([
  'topicCategory',
  'taxonomyTag',
  'additionalFunctions',
  'additionalRoles',
  'additionalTopicCategory',
  'additionalTaxonomyTag',
  'rolePriority',
  'priorityJourneyStep'
]);

const syncDerivedFields = (
  derivedProps: Record<string, Metadata[]>,
  setModalData: (data: any) => void
) => {
  setModalData((prev: any) => {
    const updatedProps = { ...prev.props };
    ALL_DERIVED_FIELDS.forEach(fieldName => {
      const manualItems = (updatedProps[fieldName] || []).filter(
        (item: any) => item?.relationship?.props?.thruCd !== ThruCd.Derived
      );
      const newDerivedItems: Metadata[] = Array.isArray(derivedProps[fieldName])
        ? derivedProps[fieldName]
        : [];
      const derivedRefIds = new Set(
        newDerivedItems.map(item => item?.props?.refId).filter(Boolean)
      );
      const manualOnlyItems = manualItems.filter(
        (item: any) => !derivedRefIds.has(item?.props?.refId)
      );
      updatedProps[fieldName] = [...manualOnlyItems, ...newDerivedItems];
    });
    return { ...prev, props: updatedProps };
  });
};

export type AdditionalQuestionsModalProps = {
  data: Partial<Metadata>;
  schema: Schema;
  schemas?: Schema[];
  onClose: () => void;
  onSave: (newFields: FieldUpdate[]) => Promise<any>;
};

const getDisabledRefIds = (
  data: Partial<Metadata>,
  field: string
): string[] => {
  const items: Metadata[] = data?.props?.[field] || [];
  return items.map(item => item?.props?.refId).filter(Boolean);
};

const getDerivedRolePriorityRefIds = (
  data: Partial<Metadata>,
  field: string
): string[] => {
  const items: Metadata[] = data?.props?.[field] || [];
  return items
    .filter(item => item?.relationship?.props?.thruCd === ThruCd.Derived)
    .map(item => item?.props?.refId)
    .filter(Boolean);
};

const fieldNames = [
  ['function', 'primaryFunctionField'],
  ['role', 'primaryRoleField'],
  ['primaryQuestion', 'primaryQuestionField'],
  ['additionalFunctions', 'additionalFunctionsField'],
  ['additionalQuestions', 'additionalQuestionsField'],
  ['rolePriority', 'rolePriorityField'],
  ['additionalTopicCategory', 'additionalTopicCategoryField'],
  ['additionalTaxonomyTag', 'additionalTaxonomyTagField'],
  ['additionalRoles', 'additionalRolesField'],
  ['priorityJourneyStep', 'priorityJourneyStepField'],
  ['topicCategory', 'topicCategoryField'],
  ['taxonomyTag', 'taxonomyTagField']
];

export const AdditionalQuestionsModal = ({
  data,
  schema,
  schemas,
  onClose,
  onSave
}: AdditionalQuestionsModalProps) => {
  const { runRules, validateModalData } = useActions();
  const initialModalProps = React.useMemo(() => {
    return Object.keys(data.props || {}).reduce(
      (acc, key) => {
        if (fieldNames.some(([field]) => field === key)) {
          acc[key] = data.props[key];
        }
        return acc;
      },
      {} as Record<string, any>
    );
  }, [data]);
  const [modalData, setModalData] = React.useState<any>(() => {
    return { ...data, props: initialModalProps, metadata: [] };
  });
  const [isSaving, setIsSaving] = React.useState(false);
  const [isValidating, setIsValidating] = React.useState(false);
  const [validateType, setValidateType] = React.useState<
    null | 'save' | 'derive'
  >(null);
  const [shouldValidate, setShouldValidate] = React.useState<ValidationMode>(
    ValidationMode.None
  );
  const [validationErrors, setValidationErrors] =
    React.useState<ValidationResponse | null>(null);

  const fieldVars: Record<string, SchemaField | undefined> = {};
  fieldNames.forEach(([field, varName]) => {
    fieldVars[varName] = findOne(schema?.props?.fields, {
      field
    }) as SchemaField;
  });

  const primaryFunctionField = fieldVars['primaryFunctionField'];
  const primaryQuestionField = fieldVars['primaryQuestionField'];
  const additionalFunctionsField = fieldVars['additionalFunctionsField'];
  const additionalQuestionsField = fieldVars['additionalQuestionsField'];
  const rolePriorityField = fieldVars['rolePriorityField'];
  const additionalTopicCategoryField =
    fieldVars['additionalTopicCategoryField'];
  const topicCategoryField = fieldVars['topicCategoryField'];

  const primaryFnSettings = primaryFunctionField
    ? TreeFieldUtils.getTreeSetting(schema, primaryFunctionField, schemas)
    : null;
  const primaryQuestionSettings = primaryQuestionField
    ? TreeFieldUtils.getTreeSetting(schema, primaryQuestionField, schemas)
    : null;
  const fnRoleSettings = additionalFunctionsField
    ? TreeFieldUtils.getTreeSetting(schema, additionalFunctionsField, schemas)
    : null;
  const additionalQuestionsSettings = additionalQuestionsField
    ? TreeFieldUtils.getTreeSetting(schema, additionalQuestionsField, schemas)
    : null;
  const rolePrioritySettings = rolePriorityField
    ? TreeFieldUtils.getTreeSetting(schema, rolePriorityField, schemas)
    : null;
  const additionalTopicCategorySettings = additionalTopicCategoryField
    ? TreeFieldUtils.getTreeSetting(
        schema,
        additionalTopicCategoryField,
        schemas
      )
    : null;

  const topicCategorySettings = topicCategoryField
    ? TreeFieldUtils.getTreeSetting(schema, topicCategoryField, schemas)
    : null;

  const existingFnRefIds = React.useMemo(
    () => [
      ...getDisabledRefIds(data, 'additionalFunctions'),
      ...getDisabledRefIds(data, 'additionalRoles')
    ],
    [data]
  );
  const existingQuestionRefIds = React.useMemo(
    () => getDisabledRefIds(data, 'additionalQuestions'),
    [data]
  );
  const existingRolePriorityRefIds = React.useMemo(
    () => [
      ...getDerivedRolePriorityRefIds(data, 'rolePriority'),
      ...getDerivedRolePriorityRefIds(data, 'priorityJourneyStep')
    ],
    [data]
  );

  const isFnDisabled = React.useCallback(
    (node: Node<Metadata>) => existingFnRefIds.includes(node.key),
    [existingFnRefIds]
  );
  const isQuestionDisabled = React.useCallback(
    (node: Node<Metadata>) => existingQuestionRefIds.includes(node.key),
    [existingQuestionRefIds]
  );
  const isRolePriorityDisabled = React.useCallback(
    (node: Node<Metadata>) => existingRolePriorityRefIds.includes(node.key),
    [existingRolePriorityRefIds]
  );

  const isWipItem = React.useMemo(() => {
    const statusId = data?.props?.status?.[0]?.props?.id;
    return isWIPProductionPhase(statusId);
  }, [data?.props?.status]);

  const requiredTopicCategoryField = React.useMemo(() => {
    if (!topicCategoryField) return topicCategoryField;
    return {
      ...topicCategoryField,
      requiredAt: FieldRequiredValue.Required
    } as SchemaField;
  }, [topicCategoryField]);

  const requiredPrimaryFunctionField = React.useMemo(() => {
    if (!primaryFunctionField) return primaryFunctionField;
    return {
      ...primaryFunctionField,
      requiredAt: FieldRequiredValue.Required
    } as SchemaField;
  }, [primaryFunctionField]);

  const hasChanges = React.useMemo(
    () => !deepEquals(modalData?.props || {}, initialModalProps || {}),
    [modalData?.props, initialModalProps]
  );

  const isSaveDisabled = !hasChanges;

  const handleFieldUpdate = async (
    fieldsToUpdate: { field: SchemaField; value: Metadata[] }[]
  ) => {
    setModalData((prev: any) => {
      const updatedProps = { ...prev.props };
      fieldsToUpdate.forEach(f => {
        updatedProps[f.field.field] = f.value;
      });
      return { ...prev, props: updatedProps };
    });

    const questionUpdate = fieldsToUpdate.find(
      f =>
        f.field.field === 'additionalQuestions' ||
        f.field.field === 'primaryQuestion'
    );
    const shouldRunRules = questionUpdate;
    if (shouldRunRules && runRules) {
      setValidateType('derive');
      setIsValidating(true);
      try {
        const updatedModalData = {
          labels: Array.isArray(modalData.labels)
            ? [...modalData.labels]
            : ([NodeType.ContentItem] as NodeType[]),
          props: {
            itemType: ItemType.content,
            primaryQuestion:
              questionUpdate.field.field === 'primaryQuestion'
                ? questionUpdate.value
                : (modalData.props.primaryQuestion ?? []),
            additionalQuestions:
              questionUpdate.field.field === 'additionalQuestions'
                ? questionUpdate.value
                : (modalData.props.additionalQuestions ?? [])
          }
        };
        const rulesResponse = await runRules(updatedModalData);
        syncDerivedFields(rulesResponse?.props ?? {}, setModalData);
      } catch (e) {
        console.error('Audience derivation failed', e);
      } finally {
        setIsValidating(false);
        setValidateType(null);
      }
    }
  };

  const modalFieldKeys = React.useMemo(() => {
    const keys = new Set<string>();
    const allSettings = [
      primaryFnSettings,
      primaryQuestionSettings,
      fnRoleSettings,
      additionalQuestionsSettings,
      rolePrioritySettings,
      additionalTopicCategorySettings,
      topicCategorySettings
    ];
    const allFields = [
      primaryFunctionField,
      primaryQuestionField,
      additionalFunctionsField,
      additionalQuestionsField,
      rolePriorityField,
      additionalTopicCategoryField,
      topicCategoryField
    ];
    allFields.forEach(f => {
      if (f?.key) keys.add(f.key);
    });
    allSettings.forEach(ts => {
      ts?.settings?.forEach(s => {
        if (s?.field?.key) keys.add(s.field.key);
      });
    });
    return keys;
  }, [
    primaryFnSettings,
    primaryQuestionSettings,
    fnRoleSettings,
    additionalQuestionsSettings,
    rolePrioritySettings,
    additionalTopicCategorySettings,
    topicCategorySettings
  ]);

  const handleSave = async () => {
    setShouldValidate(ValidationMode.Current);
    setIsSaving(true);
    setValidateType('save');
    setIsValidating(true);
    setValidationErrors(null);
    try {
      const pushResponse = await validateModalData(modalData);
      setIsValidating(false);
      setValidateType(null);
      const modalFieldErrors = (pushResponse?.errors || []).filter(e =>
        modalFieldKeys.has(e.key)
      );
      const modalFieldWarnings = (pushResponse?.warnings || []).filter(e =>
        modalFieldKeys.has(e.key)
      );

      const requiredFieldValidations = [
        getValidationErrors({
          field: requiredTopicCategoryField,
          value: modalData?.props?.topicCategory,
          data: modalData,
          shouldValidate: ValidationMode.Current
        }),
        getValidationErrors({
          field: requiredPrimaryFunctionField,
          value: modalData?.props?.function,
          data: modalData,
          shouldValidate: ValidationMode.Current
        })
      ];

      const frontendFieldErrors = requiredFieldValidations
        .flatMap(v => v?.errors || [])
        .filter(e => modalFieldKeys.has(e.key));

      frontendFieldErrors.forEach(fe => {
        const exists = modalFieldErrors.some(
          be =>
            be?.key === fe?.key &&
            be?.validationMessage === fe?.validationMessage
        );
        if (!exists) {
          modalFieldErrors.push(fe as any);
        }
      });

      const errorCount = modalFieldErrors.length + modalFieldWarnings.length;
      if (errorCount > 0) {
        setValidationErrors({
          ...pushResponse,
          errors: modalFieldErrors,
          warnings: modalFieldWarnings
        });
        return;
      }

      const fieldUpdates = Object.entries(modalData.props || {})
        .filter(([, value]) => Array.isArray(value) && value.length > 0)
        .map(([fieldName, value]) => {
          const schemaField = (schema?.props?.fields || []).find(
            (f: any) => f.field === fieldName
          );
          return schemaField ? { field: schemaField, value } : null;
        })
        .filter(Boolean) as FieldUpdate[];
      await onSave(fieldUpdates);
    } catch (e) {
    } finally {
      setIsSaving(false);
      setIsValidating(false);
      setValidateType(null);
    }
  };

  return (
    <Modal
      id="additionalQuestionsModal"
      header="Update Audience Metadata"
      onClose={onClose}
      footer={
        <Buttons>
          <Secondary label="Cancel" onClick={onClose} disabled={isSaving} />
          <Primary
            label="Save"
            onClick={handleSave}
            disabled={isSaveDisabled || isSaving}
          />
        </Buttons>
      }
    >
      <div className="modal-validation-sticky">
        {isPublishedOrCompletedInCPP(data?.props?.status?.[0]?.props?.id) && (
          <div className="republish-warning">
            {`Please note that going ahead with this action will trigger a re-publish`}
          </div>
        )}
        {isValidating && (
          <Alert className="metadata-validation-alert" type="info" loading>
            {validateType === 'save'
              ? 'Validating...'
              : 'Deriving audience data...'}
          </Alert>
        )}
        {!isValidating &&
          validationErrors &&
          (() => {
            const numErrors =
              (validationErrors.errors?.length || 0) +
              (validationErrors.warnings?.length || 0);
            return (
              <Alert className="metadata-validation-alert" type="error">
                <b>Incomplete Information! </b>There
                {numErrors === 1 ? ' is ' : ' are '}
                <b>{numErrors}</b> item
                {numErrors === 1 ? '' : 's'} which need
                {numErrors === 1 ? 's' : ''} your attention
              </Alert>
            );
          })()}
      </div>
      <ModalPushResponseContext.Provider value={validationErrors}>
        <div className="additional-questions-modal-fields">
          {[
            {
              settings: primaryFnSettings,
              field: primaryFunctionField,
              disabled: !isWipItem
            },
            {
              settings: primaryQuestionSettings,
              field: primaryQuestionField,
              disabled: !isWipItem
            },
            {
              settings: fnRoleSettings,
              field: additionalFunctionsField,
              disabled: false,
              isDisabled: isFnDisabled
            },
            {
              settings: additionalQuestionsSettings,
              field: additionalQuestionsField,
              disabled: false,
              isDisabled: isQuestionDisabled
            },
            {
              settings: rolePrioritySettings,
              field: rolePriorityField,
              disabled: false,
              isDisabled: isRolePriorityDisabled
            },
            {
              settings: additionalTopicCategorySettings,
              field: additionalTopicCategoryField,
              disabled: true
            },
            {
              settings: topicCategorySettings,
              field: topicCategoryField,
              disabled: true
            }
          ].map(({ settings, field, disabled, isDisabled }, idx) =>
            settings && field ? (
              <div key={field.field}>
                <TreeField
                  {...settings}
                  schema={schema}
                  field={
                    field?.field === 'function'
                      ? requiredPrimaryFunctionField
                      : field?.field === 'topicCategory'
                        ? requiredTopicCategoryField
                        : field
                  }
                  data={modalData}
                  disabled={disabled}
                  isDisabled={isDisabled}
                  hideNodeMenu
                  shouldValidate={shouldValidate}
                  updater={{
                    updateFields: (fieldsToUpdate: any[]) => {
                      if (!fieldsToUpdate?.length) return;
                      handleFieldUpdate(fieldsToUpdate as FieldUpdate[]);
                    },
                    update: () => Promise.resolve(),
                    reset: () => {},
                    updateField: () => {}
                  }}
                />
              </div>
            ) : null
          )}
        </div>
      </ModalPushResponseContext.Provider>
    </Modal>
  );
};
